<template>
  <div class="w-full text-textPrimary">
    <div v-if="stepSelected < stepLabels.length" class="pb-5 mb-6 border-b border-gray-200">
      <ScheduleStepper :steps="stepLabels" :current="stepSelected" />
    </div>

    <div data-testid="create-schedule-step-content" class="mb-8">
      <ScheduleBasicInfo
        v-if="stepSelected === 0"
        :errors="errors"
        :onClearError="clearError"
        @open-import-modal="isImportModalOpen = true"
      />
      <ScheduleRequirements
        v-else-if="stepSelected === 1"
        :errors="errors"
        :onClearError="clearError"
      />
      <ExchangeConfigForm
        v-else-if="stepSelected === 2"
        :errors="errors"
        :onClearError="clearError"
      />
      <DsAttendancePolicy
        v-else-if="stepSelected === 3"
        ref="dsAttendancePolicyRef"
        createSchedule
        :full-width-modal="true"
        :initial-data="step3"
        :schedule-end-date="step1.endDate?.toISOString() ?? null"
        @save="handleAttendanceSave"
      />
      <ScheduleApprovalFlow
        v-else-if="showExceptionalityStep && stepSelected === approvalFlowStepIndex"
        :errors="errors"
        :onClearError="clearError"
        :show-errors="showApprovalErrors"
      />
      <CreatePaymentPolicy
        v-else-if="stepSelected === paymentStepIndex"
        ref="createPaymentPolicyRef"
        mode="create"
        :errors="errors"
        :onClearError="clearError"
      />
    </div>

    <div class="w-full flex justify-end gap-4 mt-5">
      <outlined-button
        :text="stepSelected === 0 ? 'Cancelar' : 'Voltar'"
        width="140px"
        height="48px"
        @click="stepSelected === 0 ? handleCancel() : handleStep('back')"
      />
      <filled-button
        data-testid="create-schedule-avancar-button"
        :text="stepSelected === paymentStepIndex ? 'Salvar e Criar Escala' : 'Avançar'"
        width="200px"
        height="48px"
        :loading="isSaving"
        :disabled="isNextDisabled"
        @click="handleStep('next')"
        class="bg-primary30"
      />
    </div>

    <!-- Mesmo gate do botão em ScheduleBasicInfo: com a flag off o modal nem monta,
         então não existe caminho para a chamada de importação em produção. -->
    <ImportScheduleConfigModal
      v-if="flags.enabledPolicyModules"
      :modalActive="isImportModalOpen"
      :isImporting="isImportingConfig"
      @close="isImportModalOpen = false"
      @select="handleImportSchedule"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, provide } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { router } from '@/router'
import {
  useCreateScheduleStore,
  CREATE_SCHEDULE_ERROR,
  IMPORT_CONFIGURATION_ERROR
} from '@/pinia-store/useCreateScheduleStore'
import { allRulesHaveRoles, groupToApiLayers } from '@/utils/approval-flow-transformers'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import {
  isTeamHandoverConfigValid,
  TEAM_HANDOVER_VALIDATION_RULES,
  TEAM_CONSECUTIVE_HOURS_VALIDATION_RULES,
  type TeamHandoverConfig
} from '@/constants/handover.constants'
import { SCHEDULE_SECTION_LABELS } from '@/constants/schedule-sections.constants'
import * as yup from 'yup'
import { step1Schema, step2Schema, step4Schema, stepExceptionalidadeSchema } from './schemas'
import { dropImportedUnitIfUnavailable } from './importGuards'

import ScheduleBasicInfo from './ScheduleBasicInfo.vue'
import ScheduleStepper from './ScheduleStepper.vue'
import ScheduleRequirements from './ScheduleRequirements.vue'
import ExchangeConfigForm from './ExchangeConfigForm.vue'
import DsAttendancePolicy from './DsAttendancePolicy.vue'
import ScheduleApprovalFlow from './ScheduleApprovalFlow.vue'
import CreatePaymentPolicy from './CreatePaymentPolicy.vue'
import ImportScheduleConfigModal from './modals/ImportScheduleConfigModal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'

const store = useCreateScheduleStore()
provide('createScheduleStore', store)
const headerStore = useHeaderStore()
const toast = useToast()
const { notifyError } = useErrorToast()
const { flags } = useFeatureFlags()
const permissionsStore = usePermissionsStore()
const { step1, step2, stepExchangeConfig, step3, step4, stepApprovalFlow } = storeToRefs(store)

const stepSelected = ref(0)
const errors = ref<Record<string, string>>({})
const isSaving = ref(false)
const showApprovalErrors = ref(false)
const dsAttendancePolicyRef = ref<InstanceType<typeof DsAttendancePolicy> | null>(null)
const createPaymentPolicyRef = ref<any>(null)
const isImportModalOpen = ref(false)
const isImportingConfig = ref(false)
const healthUnitStore = useHealthUnitStore()

const canViewExceptionality = computed(() =>
  permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationExceptionalitiesExceptionViewing,
    resourceType: AuthorizationResourcesAndSubjects.StationExceptionalitiesSchedule
  })
)

const showExceptionalityStep = computed(
  () => flags.enabledApprovalFlow && canViewExceptionality.value
)

const stepLabels = computed(() => [
  SCHEDULE_SECTION_LABELS.basicInfo,
  SCHEDULE_SECTION_LABELS.requirements,
  SCHEDULE_SECTION_LABELS.exchangeConfig,
  SCHEDULE_SECTION_LABELS.attendancePolicy,
  ...(showExceptionalityStep.value ? [SCHEDULE_SECTION_LABELS.exceptionality] : []),
  SCHEDULE_SECTION_LABELS.paymentPolicy
])

const MANAGE_SCHEDULE_ROUTE = '/gerenciar-escala'
const approvalFlowStepIndex = 4
const paymentStepIndex = computed(() => (showExceptionalityStep.value ? 5 : 4))

const isNextDisabled = computed(() => {
  if (stepSelected.value === 2) {
    const ec = stepExchangeConfig.value
    return !(isTeamHandoverConfigValid(ec.ourTeam) && isTeamHandoverConfigValid(ec.notOurTeam))
  }
  if (stepSelected.value === 3) {
    // M7, balão ⑫ — "Avançar" é o "Salvar política" deste passo: fica bloqueado
    // enquanto a confirmação do desligamento do check não estiver marcada.
    return (
      (dsAttendancePolicyRef.value?.isCheckMethodMissing ?? false) ||
      (dsAttendancePolicyRef.value?.isCheckDisableUnconfirmed ?? false)
    )
  }
  return false
})

function validateTeamInto(config: TeamHandoverConfig, team: 'ourTeam' | 'notOurTeam') {
  // Jornada é validada sempre — independe da restrição de trocas.
  if (config.limitConsecutiveHours) {
    for (const [field, message] of Object.entries(TEAM_CONSECUTIVE_HOURS_VALIDATION_RULES)) {
      if (!config[field as keyof TeamHandoverConfig]) errors.value[`${team}.${field}`] = message
    }
  }
  if (!config.allowDoctorStationHandover) return
  for (const [field, message] of Object.entries(TEAM_HANDOVER_VALIDATION_RULES)) {
    if (!config[field as keyof TeamHandoverConfig]) errors.value[`${team}.${field}`] = message
  }
}

function validateExchangeConfig(): boolean {
  validateTeamInto(stepExchangeConfig.value.ourTeam, 'ourTeam')
  validateTeamInto(stepExchangeConfig.value.notOurTeam, 'notOurTeam')
  return Object.keys(errors.value).length === 0
}

async function validateApprovalFlow(): Promise<void> {
  if (!allRulesHaveRoles(stepApprovalFlow.value.layers[0])) {
    showApprovalErrors.value = true
    throw new yup.ValidationError('Todas as regras devem ter um papel validador.', null, 'layers')
  }

  showApprovalErrors.value = false

  // Valida exatamente o que vai para a API. O achatamento anterior colapsava os
  // subgrupos numa pseudo-camada com o operador da raiz, então o schema
  // enxergava uma forma diferente do payload: a regra de "OU exige 2 papéis"
  // nunca era checada nos subgrupos e só aparecia no 400 do save.
  const layers = groupToApiLayers(stepApprovalFlow.value.layers[0])

  if (layers.length > 0) {
    await stepExceptionalidadeSchema.validate({ layers }, { abortEarly: false })
  }
}

/** Qualificação exigida inválida trava o passo de requisitos (medical-docs-qualification). */
function isQualificationRequirementInvalid(): boolean {
  if (!flags.enabledProfessionalDocuments || store.stepQualificationRequirement.isValid)
    return false
  errors.value.qualificationPolicy = 'Corrija a Qualificação exigida para avançar.'
  return true
}

async function validateAndAdvanceStep(): Promise<void> {
  const step = stepSelected.value

  if (step === 0) {
    await step1Schema.validate(step1.value, { abortEarly: false })
    stepSelected.value++
    return
  }
  if (step === 1) {
    await step2Schema.validate(step2.value, { abortEarly: false })
    if (isQualificationRequirementInvalid()) return
    stepSelected.value++
    return
  }
  if (step === 2) {
    if (!validateExchangeConfig()) return
    stepSelected.value++
    return
  }
  if (step === 3) {
    await dsAttendancePolicyRef.value?.submitPolicy?.()
    return
  }
  if (step === approvalFlowStepIndex && showExceptionalityStep.value) {
    await validateApprovalFlow()
    stepSelected.value++
    return
  }
  if (step === paymentStepIndex.value) await handleSaveSchedule()
}

function collectYupErrors(err: any) {
  if (!err.inner) return
  err.inner.forEach((e: any) => {
    if (e.path) {
      const key = e.path.replace(/\[\d+\]$/, '')
      errors.value[key] = e.message
    }
  })
}

async function handleStep(direction: 'next' | 'back') {
  if (direction === 'back') {
    stepSelected.value--
    return
  }

  errors.value = {}

  try {
    await validateAndAdvanceStep()
  } catch (err: any) {
    collectYupErrors(err)
  }
}

function handleAttendanceSave(data: any) {
  store.setDsAttendancePolicy(data)
  errors.value = {}
  stepSelected.value++
}

async function handleSaveSchedule() {
  try {
    const nameValid = createPaymentPolicyRef.value?.validateAndConfirmName()
    if (nameValid === false) {
      errors.value['name'] = 'Informe o nome da política de pagamento.'
      return
    }
    await step4Schema.validate(step4.value, {
      abortEarly: false,
      context: { scheduleEndDate: step1.value.endDate?.toISOString() ?? null }
    })
    if (stepApprovalFlow.value.enabled) {
      await validateApprovalFlow()
    }
    isSaving.value = true
    // BUG-PP-06: os vínculos das políticas de qualificação replicadas (RN-16)
    // agora viajam dentro do payload do full-create — o backend cria tudo na
    // mesma transação, então não há mais pós-processamento nem aviso parcial.
    await store.createFullSchedule()

    toast.success('Escala criada com sucesso!')
    router.push(MANAGE_SCHEDULE_ROUTE)
  } catch (err: any) {
    collectYupErrors(err)
    if (!err.inner) {
      // `err?.message` vazava texto técnico de TypeError e ignorava rede/timeout;
      // notifyError passa pelo funil canônico e reconhece o ServiceError do service.
      notifyError(err, CREATE_SCHEDULE_ERROR)
    }
  } finally {
    isSaving.value = false
  }
}

function clearImportedUnit() {
  step1.value.healthUnitId = null
  step1.value.healthUnitName = ''
}

async function handleImportSchedule(scheduleId: number) {
  isImportingConfig.value = true
  try {
    await store.importFromSchedule(scheduleId)
    isImportModalOpen.value = false

    // A guarda só decide corretamente com a lista carregada (ver importGuards.ts) —
    // o fetch do onMounted deste componente normalmente já resolveu, mas se o
    // usuário importar rápido demais (ou a lista ainda não tiver sido buscada por
    // algum outro motivo), busca aqui para não deixar a decisão errada por lista vazia.
    if (!healthUnitStore.healthUnitsAdminAccess.length) {
      await healthUnitStore.getHealthUnitOptions(true)
    }

    // Fetch falhou 2x (onMounted + retry acima) — sem lista não há como validar a
    // unidade importada; conservador (descartar) é mais seguro que deixar o id órfão sobreviver.
    if (!healthUnitStore.healthUnitsAdminAccess.length) {
      clearImportedUnit()
      toast.warning(
        'Configurações importadas. Não foi possível validar a unidade da escala de origem — selecione a unidade manualmente.'
      )
      return
    }

    if (dropImportedUnitIfUnavailable(healthUnitStore.healthUnitsAdminAccess, step1.value)) {
      clearImportedUnit()
      toast.success(
        'Configurações importadas. A unidade da escala de origem não está disponível para o seu usuário — selecione outra unidade para continuar.'
      )
      return
    }
    toast.success('Configurações importadas com sucesso!')
  } catch (err) {
    // O `catch {}` sem binding descartava o erro inteiro — a mensagem do backend
    // (ex.: escala de origem sem permissão) nunca chegava ao usuário.
    notifyError(err, IMPORT_CONFIGURATION_ERROR)
  } finally {
    isImportingConfig.value = false
  }
}

const clearError = (field: string) => delete errors.value[field]
const handleCancel = () => {
  if (confirm('Deseja sair?')) router.push('/gerenciar-escala')
}

onMounted(() => {
  // Reset defensivo: o modal de politica de pagamento (edicao de escala)
  // compartilha o step4 desta store; garante que vigencia/regras de uma
  // edicao anterior nao vazem para um wizard de criacao recem-montado.
  store.resetStep4()
  headerStore.setTitle('Criar Escala', 'Configure os detalhes da nova escala de trabalho', {
    label: 'Gerenciar Escalas',
    route: MANAGE_SCHEDULE_ROUTE
  })

  // Garante a lista de unidades disponível antes que a guarda de import precise
  // decidir (dropImportedUnitIfUnavailable) — ManageScale/Schedule/index.vue já
  // faz esse fetch no mount da listagem, mas /criar-escala pode ser acessado
  // direto (refresh, link, nova aba), sem passar por lá antes. Idempotente: se
  // já houver dados, o refetch é barato e apenas mantém a lista atualizada.
  healthUnitStore.getHealthUnitOptions(true)
})
watch(stepSelected, () => {
  if (stepSelected.value === paymentStepIndex.value)
    headerStore.setTitle(`Escala de ${step1.value.scheduleName}`, '', {
      label: 'Gerenciar Escalas',
      route: MANAGE_SCHEDULE_ROUTE
    })
})
</script>
